<template>
  <b-row>
    <template v-if="status === null">
      <b-col cols="12">
          <p><strong>Recipients</strong><br></p>
          <p class="text-muted">Place every recipient in a new line in the following format:<br/>
            Full Name, Email Address, Tracking Code (optional)</p>
        <b-textarea
          v-model="emailAddressesText"
          placeholder="Mary Doe, mary.joe@reallinks.io, ID1234"
        />
      </b-col>
      <b-col sm="12">
        <b-btn v-if="channelID" class="mt-3 float-right" @click="input(true)" variant="primary">
          <template v-if="!$apollo.loading">
            Send Test Message in Selected Channel
            <font-awesome-icon class="ml-2" icon="paper-plane"/>
          </template>
          <template v-else>
            <b-spinner small type="grow" variant="light"></b-spinner>
            Validating...
          </template>
        </b-btn>
        <b-btn class="mt-3 float-right" @click="input(false)" variant="primary" :disabled="$apollo.loading || !recipients || !recipients.length || loading">
          <template v-if="!$apollo.loading && !loading">
            Send Test Message
            <font-awesome-icon class="ml-2" icon="paper-plane"/>
          </template>
          <template v-else>
            <b-spinner small type="grow" variant="light"></b-spinner>
            Validating...
          </template>
        </b-btn>
      </b-col>
      <b-col sm="12">
        <p class="mt-3">
          If the message is not delievered in 30 seconds, please click on the "Send Test Message" button again.
        </p>
      </b-col>
    </template>
    <template v-else-if="status === 'success'">
      <b-col cols="12">
        <p>✅ Test message is scheduled to be sent. It can take up to a minute to be sent.</p>
      </b-col>
    </template>
    <template v-else-if="status === 'error'">
      <b-col cols="12">
        <p>❌ Test message failed to be scheduled. Please try again. In case the problem persists, please contact support we will be happy to help.</p>
      </b-col>
    </template>
  </b-row>
</template>

<script>
import gql from 'graphql-tag';
import { mapRoot } from '@src/mixins/root';

const demoEmailAddressMap = {
  // Sarah Jane
  'kathleen@demo.reallinks.io': 'sarahjane.grant@reallinks.io',
  // Hedi
  'dean@demo.reallinks.io': 'hedi.ouardani@reallinks.io',
  // Sam
  'nadine@demo.reallinks.io': 'sam.davies@reallinks.io',
};

const UsersQuery = gql`
  query TestSlackMessageUsers(
    $usersWhere: UserWhereManyInput,
    $getJobs: Boolean = false,
    $jobsWhere: JobWhereManyInput,
    $getContentShares: Boolean = false,
    $contentSharesWhere: ContentShareWhereManyInput
  ) {
    company {
      id
      users(where: $usersWhere) {
        id
        emailAddress
        jobs(where: $jobsWhere) @include(if:$getJobs) {
          id
          title
          excerpt
          shareCode
          linkedInSearchURL
          customFieldsHash
        }
        contentShares(where: $contentSharesWhere) @include(if:$getContentShares)  {
          id
          title
          description
          shareCode
          customFields
        }
      }
    }
  }
`;

const FromUserQuery = gql`
  query TestFromUser(
    $emailAddress: String,
  ) {
    company {
      id
      users(
        where: {
          NOT: { state: null },
          emailAddress: $emailAddress
        }
      ) {
          id
          emailAddress
          fullName
          state {
            microsoft
            slack
            gmail
            emailSignature
          }
      }
    }
  }
`;

const SendTestMessageMutation = gql`
  mutation SendTestMessage($input: String!) {
    sendTestMessage(input: $input)
  }
`;

export default {
  props: {
    body: {
      type: String,
    },
    subject: {
      type: String,
    },
    variables: {
      type: String,
    },
    from: {
      type: String,
    },
    job: {
      type: Object,
    },
    jobs: {
      type: Array,
    },
    contentShare: {
      type: Object,
    },
    contentShares: {
      type: Array,
    },
    collectiveGoal: {
      type: Object,
    },
    isIndividual: {
      type: Boolean,
    },
    channel: {
      type: String,
      default: 'slack',
    },
    channelID: {
      type: String
    },
    campaignID: {
      type: String,
    },
    campaignComponentID: {
      type: String,
    },
    preferences: {
      type: Object,
    },
    inputs: {
      type: Object,
    },
    template: {
      type: String,
    },
    templateDefinition: {
      type: String,
    }
  },
  data() {
    return {
      emailAddressesText: null,
      loading: false,
      status: null,
    };
  },
  apollo: {
    users: {
      query: UsersQuery,
      variables() {
        const { job, jobs, contentShare, contentShares, recipients } = this;
        const usersWhere = {
          OR: recipients.map(({ emailAddress }) => ({ emailAddress }))
        };
        console.log({
          usersWhere,
          getJobs: job || jobs,
          jobsWhere: job ? { id_in: [ job.id ] } : (Array.isArray(jobs) ? { id_in: jobs.map(({ id }) => id) } : null),
          getContentShares: !!(contentShare || contentShares),
          contentSharesWhere: contentShare ? { id_in: [ contentShare.id ] } : (Array.isArray(contentShares) ? { id_in: contentShares.map(({ id }) => id) } : null)
        });
        return {
          usersWhere,
          getJobs: !!(job || jobs),
          jobsWhere: job ? { id_in: [ job.id ] } : (Array.isArray(jobs) ? { id_in: jobs.map(({ id }) => id) } : null),
          getContentShares: !!(contentShare || contentShares),
          contentSharesWhere: contentShare ? { id_in: [ contentShare.id ] } : (Array.isArray(contentShares) ? { id_in: contentShares.map(({ id }) => id) } : null)
        };
      },
      update({ company }) {
        const { users } = company || {};
        return Array.isArray(users) ? users : null;
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      deep: true,
      skip() {
        const { recipients } = this;
        return !Array.isArray(recipients) || !recipients.length;
      }
    },
    fromUser: {
      query: FromUserQuery,
      variables() {
        return { emailAddress: this.from };
      },
      update({ company }) {
        const { users } = company || {};
        return Array.isArray(users) ? users[0] : null;
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      deep: true,
      skip() {
        const { from } = this;
        return !(from || '').includes('@');
      }
    },
  },
  computed: {
    ...mapRoot([ 'company' ]),
    recipients() {
      const { emailAddressesText } = this;
      const lines = (emailAddressesText || '').split('\n')
        .filter(l => l && l.includes(',') && l.includes('@'));
      return lines.map(el => {
        const fullName = el.split(',')[0].trim();
        const emailAddress = el.split(',')[1].trim();
        const trackingCode = el.split(',')[2] ? el.split(',')[2].trim() : null;
        const firstName = fullName.split(' ')[0].trim();
        const lastName = (fullName.split(' ')[1] || '').trim();
        return {
          emailAddress,
          firstName,
          lastName,
          trackingCode,
          points: 0,
          position: 15
        };
      });
    }
  },
  methods: {
    async input(sendInChannel) {
      const {
        campaignID,
        campaignComponentID,
        recipients,
        body,
        fromUser,
        job,
        jobs,
        contentShare,
        contentShares,
        channelID,
        channel,
        isIndividual,
        subject,
        template,
        templateDefinition,
        inputs,
        preferences,
      } = this;
      let messageType = null;
      switch (channel) {
        case 'slack':
          messageType = 'slackMessage';
          break;
        case 'email':
          messageType = 'emailMessage';
          break;
        case 'teams':
          messageType = 'teamsMessage';
          break;
        case 'push':
          messageType = 'PUSH';
          break;
      }
      const from = demoEmailAddressMap[fromUser.emailAddress] || fromUser.emailAddress;
      const { emailSignature: signature } = fromUser.state;

      const payload = {
        stage: 'TEST',
        campaignComponentID,
        campaignID,
        from,
        channelID: channelID && channelID.length && sendInChannel ? channelID : undefined,
        sendAt: (new Date()).getTime(),
        subject,
        replacedBody: signature ? body.replace('<!-- %%SIGNATURE%% -->', signature) : body,
        template,
        templateDefinition,
        inputs,
        preferences,
        type: messageType,
        target: isIndividual ? 'INDIVIDUAL' : 'GROUP',
        leaderboardTop: [
          {
            id: '1',
            position: 1,
            points: 100,
            firstName: 'John',
            lastName: 'Doe',
          },
          {
            id: '2',
            position: 2,
            points: 90,
            firstName: 'Jane',
            lastName: 'Doe',
          },
          {
            id: '3',
            position: 3,
            points: 80,
            firstName: 'John',
            lastName: 'Doe',
          }
        ],
        job,
        jobs,
        contentShare,
        contentShares,
        recipients,
      };

      console.log({ payload });

      try {
        this.loading = true;
        const result = await this.$apollo.mutate({
          mutation: SendTestMessageMutation,
          variables: { input: JSON.stringify(payload) },
        });
        console.log('Test message sent', result);
        if (result.data.sendTestMessage === 'SUCCESS') {
          this.status = 'success';
        } else {
          this.status = 'error';
        }
      } catch (error) {
        this.$notifyErr(error);
        console.log(error);
        this.status = 'error';
      } finally {
        this.loading = false;
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/test-message-form.vue